import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import logo from '../assets/logo.png'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { useAppDispatch } from '../hooks/useAppDispatch'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { setIsAuthenticated } from '../reducers/authReducer'
import { createSession } from '../services/authServices'
import { JWT_TOKEN_KEY_NAME } from '../utils/constants'

const signInForm = z.object({
  email: z.string().email('Email inválido.'),
  password: z.string().min(6, 'A senha precisa ter pelo menos 6 caracteres.'),
})

type SignInForm = z.infer<typeof signInForm>

export function SignIn() {
  const [searchParams] = useSearchParams()

  const userEmail = searchParams.get('userEmail')

  const [, setJwtToken] = useLocalStorage(JWT_TOKEN_KEY_NAME)
  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
    clearErrors,
  } = useForm<SignInForm>({
    resolver: zodResolver(signInForm),
  })

  async function handleSignIn({ email, password }: SignInForm) {
    clearErrors()
    const result = await createSession({ email, password })
    if (result) {
      if (result.success) {
        setJwtToken(result.data.token)
        navigate('/dashboard')
      } else {
        dispatch(setIsAuthenticated(false))
        setError('email', { message: result.message })
        setError('password', { message: result.message })
        toast.error(result.message, { position: 'top-left' })
      }
    }
  }

  useEffect(() => {
    if (userEmail) {
      setValue('email', userEmail)
    }
  }, [setValue, userEmail])

  return (
    <div className="flex h-[100dvh] w-[100vw] flex-1 flex-col justify-center items-center">
      <div className="max-w-md sm:w-[90vw]">
        <div className="flex flex-col flex-1 justify-center items-center w-full">
          <img
            src={logo}
            alt="Relatórios de Trademarketing"
          />
          <span className="text-center font-semibold mt-4">Relatórios de Trademarketing</span>
          <h3 className="text-center mt-9 text-2xl/9 font-bold tracking-tight text-gray-900">
            Acesse o painel
          </h3>
        </div>

        <div className="mt-10 w-full">
          <form
            className="flex-1 space-y-6"
            onSubmit={handleSubmit(handleSignIn)}
          >
            <div>
              <Input
                label="Email"
                id="email"
                type="email"
                placeholder="exemplo@email.com"
                error={errors.email}
                {...register('email')}
              />
            </div>
            <div>
              <Input
                label="Senha"
                id="password"
                type="password"
                placeholder="******"
                error={errors.password}
                {...register('password')}
              />
            </div>
            <div className="space-y-2">
              <Button
                type="submit"
                className="mb-2"
                isLoading={isSubmitting}
              >
                Acessar
              </Button>
              <Link to="/have-no-password">
                <Button
                  type="button"
                  variant="ghost"
                  isLoading={isSubmitting}
                >
                  Ainda não tenho senha
                </Button>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
